import { useState } from "react";
import { FileText, ShieldCheck, Briefcase, Clock, Send, CheckCircle2, MessageCircle, Phone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { useI18n } from "@/lib/i18n";
import { api, errMsg, useBlobUrl, fmtDate } from "@/lib/api";
import { tone } from "@/lib/constants";

export function useAct(reload) {
  const [busy, setBusy] = useState(false);
  const act = async (path, body = {}, ok) => {
    setBusy(true);
    try { await api.post(path, body); if (ok) toast.success(ok); await reload(); }
    catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  return [busy, act];
}

export function EstimateView({ est }) {
  const { t } = useI18n();
  const rows = [["d.labor", est.labor], ["d.materials", est.materials]];
  return (
    <div data-testid="estimate-view">
      <dl className="space-y-1 text-sm">
        {rows.map(([k, v]) => <div key={k} className="flex justify-between"><dt className="text-slate-500">{t(k)}</dt><dd className="font-mono">{Number(v).toFixed(2)} {est.currency}</dd></div>)}
        <div className="flex justify-between border-t pt-2 font-semibold"><dt>{t("d.total")}</dt><dd className="font-mono" data-testid="estimate-total">{Number(est.total).toFixed(2)} {est.currency}</dd></div>
      </dl>
      {est.notes && <p className="text-sm text-slate-600 mt-2 whitespace-pre-wrap">{est.notes}</p>}
      <p className="mt-2 text-xs uppercase tracking-wider font-semibold text-teal-700" data-testid="estimate-status">{t(`est.${est.status}`)}</p>
      {est.customer_note && <p className="text-xs text-red-600 mt-1">{est.customer_note}</p>}
    </div>
  );
}

export function ContactButtons({ phone, text = "", size = "sm", compact }) {
  const { t } = useI18n();
  if (!phone) return null;
  const raw = phone.replace(/\D/g, "");
  const digits = raw.length === 8 ? `505${raw}` : raw;
  const stop = (e) => e.stopPropagation();
  return (
    <div className="flex gap-1.5" onClick={stop}>
      <a href={`https://wa.me/${digits}${text ? `?text=${encodeURIComponent(text)}` : ""}`} target="_blank" rel="noreferrer" data-testid="contact-wa-link"
        className={`inline-flex items-center gap-1 rounded-full bg-[#25D366] hover:bg-[#1ebe5b] text-white font-semibold ${size === "sm" ? "text-xs px-2.5 py-1" : "text-sm px-4 py-2"}`}>
        <MessageCircle className="h-3.5 w-3.5" />{!compact && t("d.whatsapp")}
      </a>
      <a href={`tel:+${digits}`} data-testid="contact-call-link"
        className={`inline-flex items-center gap-1 rounded-full border border-slate-300 bg-white hover:bg-slate-50 text-slate-800 font-semibold ${size === "sm" ? "text-xs px-2.5 py-1" : "text-sm px-4 py-2"}`}>
        <Phone className="h-3.5 w-3.5" />{!compact && t("d.call")}
      </a>
    </div>
  );
}

export function StatusBadge({ status }) {
  const { t } = useI18n();
  return <span data-testid="status-badge" className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${tone(status)}`}>{t(`st.${status}`)}</span>;
}

export function Card({ children, className = "", ...p }) {
  return <div className={`bg-white border border-slate-200 rounded-2xl p-5 md:p-6 ${className}`} {...p}>{children}</div>;
}

export function SectionTitle({ children, ...p }) {
  return <h3 className="text-lg font-semibold text-slate-900 mb-4" {...p}>{children}</h3>;
}

export function Timeline({ history = [] }) {
  return (
    <ol className="relative border-s-2 border-teal-100 ms-2 space-y-4" data-testid="status-timeline">
      {[...history].reverse().map((h, i) => (
        <li key={i} className="ms-4">
          <span className={`absolute -start-[7px] mt-1.5 h-3 w-3 rounded-full ${i === 0 ? "bg-teal-600" : "bg-teal-200"}`} />
          <StatusBadge status={h.status} />
          {h.note && <p className="text-sm text-slate-600 mt-1">{h.note}</p>}
          <p className="text-[11px] text-slate-400 font-mono mt-0.5">{fmtDate(h.at)} · {h.by}</p>
        </li>
      ))}
    </ol>
  );
}

function AuthFile({ f }) {
  const url = useBlobUrl(f.id);
  const { t } = useI18n();
  const isImg = f.content_type?.startsWith("image/");
  const isVid = f.content_type?.startsWith("video/");
  return (
    <a href={url || undefined} target="_blank" rel="noreferrer" data-testid="file-thumb" className="group block rounded-xl overflow-hidden border border-slate-200 bg-slate-50">
      <div className="aspect-square flex items-center justify-center">
        {url && isImg && <img src={url} alt={f.original_filename} className="h-full w-full object-cover group-hover:scale-105 transition-transform" />}
        {url && isVid && <video src={url} className="h-full w-full object-cover" />}
        {(!url || (!isImg && !isVid)) && <FileText className="h-8 w-8 text-slate-400" />}
      </div>
      <p className="text-[10px] px-2 py-1 truncate text-slate-500">{f.stage ? t(`st.${f.stage}`) + " · " : ""}{f.original_filename}</p>
    </a>
  );
}

export function Gallery({ files = [] }) {
  const { t } = useI18n();
  if (!files.length) return <p className="text-sm text-slate-400">{t("d.empty")}</p>;
  return <div className="grid grid-cols-3 sm:grid-cols-4 gap-3" data-testid="file-gallery">{files.map((f) => <AuthFile key={f.id} f={f} />)}</div>;
}

export function Messages({ request, channel, onSent }) {
  const { t } = useI18n();
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);
  const list = (request.messages || []).filter((m) => m.channel === channel);
  const send = async () => {
    if (!body.trim()) return;
    setBusy(true);
    try { await api.post(`/requests/${request.id}/messages`, { body, channel }); setBody(""); onSent(); }
    catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  return (
    <div data-testid={`messages-${channel}`}>
      <div className="space-y-3 max-h-80 overflow-y-auto mb-3">
        {list.length === 0 && <p className="text-sm text-slate-400">{t("d.no_msgs")}</p>}
        {list.map((m) => (
          <div key={m.id} className="rounded-xl bg-slate-50 px-3 py-2" data-testid="message-item">
            <p className="text-xs font-semibold text-slate-700">{m.author} <span className="font-normal text-slate-400 capitalize">· {m.role}</span></p>
            <p className="text-sm text-slate-800 whitespace-pre-wrap">{m.body}</p>
            <p className="text-[10px] text-slate-400 font-mono">{fmtDate(m.at)}</p>
          </div>
        ))}
      </div>
      <div className="flex gap-2">
        <Textarea rows={2} value={body} onChange={(e) => setBody(e.target.value)} placeholder={t("d.msg_ph")} data-testid={`message-input-${channel}`} />
        <Button onClick={send} disabled={busy} className="self-end bg-teal-600 hover:bg-teal-700" data-testid={`message-send-${channel}`}><Send className="h-4 w-4" /></Button>
      </div>
    </div>
  );
}

export function ContractorCard({ c, onSelect, busy, chosen }) {
  const { t } = useI18n();
  return (
    <div className={`lift rounded-2xl border p-5 bg-white ${chosen ? "border-teal-500 ring-2 ring-teal-100" : "border-slate-200"}`} data-testid={`contractor-card-${c.id}`}>
      <div className="flex items-center gap-3">
        <div className="h-12 w-12 rounded-full bg-teal-600 text-white flex items-center justify-center font-bold text-lg">{c.name?.[0]}</div>
        <div>
          <p className="font-semibold text-slate-900" data-testid="contractor-card-name">{c.name}</p>
          <p className="text-xs font-semibold text-amber-600" data-testid="contractor-card-rating">★ {c.rating_count ? `${c.rating_avg.toFixed(1)} (${c.rating_count})` : t("rt.none")}</p>
          <p className="text-xs text-slate-500">{(c.service_areas || []).join(", ")}</p>
        </div>
      </div>
      <div className="mt-4 space-y-1.5 text-sm text-slate-600">
        <p className="flex items-center gap-2"><Briefcase className="h-4 w-4 text-teal-600" />{t("d.years", { n: c.years_experience })} · {t("d.jobs_done", { n: c.jobs_completed })}</p>
        {c.availability && <p className="flex items-center gap-2"><Clock className="h-4 w-4 text-teal-600" />{c.availability}</p>}
        {c.checks?.length > 0 && <p className="flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-teal-600" />{t("d.checked", { list: c.checks.join(", ") })}</p>}
        {c.languages?.length > 0 && <p className="text-xs text-slate-400">{c.languages.join(" · ")}</p>}
      </div>
      {onSelect && (
        <Button onClick={() => onSelect(c.id)} disabled={busy} className="mt-5 w-full rounded-full bg-teal-600 hover:bg-teal-700" data-testid={`select-contractor-${c.id}`}>
          <CheckCircle2 className="h-4 w-4 me-2" />{t("d.select")}
        </Button>
      )}
    </div>
  );
}
